import { useEffect, useState, useCallback } from "react";
import { X } from "lucide-react";

const BLOOM_DISMISSED_KEY = 'bloom-widget-dismissed';
const BLOOM_LOAD_DELAY = 10000; // 10 seconds delay before loading to reduce TBT

export const BloomWidget = () => {
  const [dismissed, setDismissed] = useState(() => {
    return sessionStorage.getItem(BLOOM_DISMISSED_KEY) === 'true';
  });
  const [widgetLoaded, setWidgetLoaded] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);

  // Deferred loading - wait for idle time or user interaction
  useEffect(() => {
    if (dismissed || sessionStorage.getItem(BLOOM_DISMISSED_KEY) === 'true') {
      setDismissed(true);
      return;
    }

    // Use requestIdleCallback for non-blocking load, fallback to setTimeout
    const scheduleLoad = () => {
      if ('requestIdleCallback' in window) {
        (window as any).requestIdleCallback(() => setShouldLoad(true), { timeout: BLOOM_LOAD_DELAY });
      } else {
        setTimeout(() => setShouldLoad(true), BLOOM_LOAD_DELAY);
      }
    };

    // Also trigger on user scroll (indicates engagement)
    const handleScroll = () => {
      setShouldLoad(true);
      window.removeEventListener('scroll', handleScroll);
    };

    // Start the timer
    const timer = setTimeout(scheduleLoad, BLOOM_LOAD_DELAY);
    
    // Listen for scroll as alternative trigger
    window.addEventListener('scroll', handleScroll, { passive: true, once: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [dismissed]);

  // Load Bloom widget only when shouldLoad is true
  useEffect(() => {
    if (!shouldLoad || dismissed) return;

    // Set Bloom settings
    (window as any).bloomSettings = {
      userId: "2gr9mqr1ldzy0",
      profileId: "j469vj6w59e0q"
    };

    // Load Bloom widget script with async/defer
    if (typeof (window as any).bloomScript === 'undefined') {
      const bloomScript = document.createElement('script');
      bloomScript.async = true;
      bloomScript.defer = true;
      
      fetch(`https://code.bloom.io/version?t=${Date.now()}`)
        .then(response => response.text())
        .then(version => {
          bloomScript.src = `https://code.bloom.io/widget.js?v=${version}`;
          document.head.appendChild(bloomScript);
          
          // Wait for Bloom widget to actually render before showing dismiss button
          setTimeout(() => {
            setWidgetLoaded(true);
          }, 3000);
        })
        .catch(() => {
          // Silently fail - widget is non-critical
          console.warn('Bloom widget failed to load');
        });
      
      (window as any).bloomScript = bloomScript;
    } else {
      setWidgetLoaded(true);
    }
  }, [shouldLoad, dismissed]);

  // Handle dismiss - aggressively remove the Bloom widget
  const handleDismiss = useCallback(() => {
    setDismissed(true);
    
    // Store in sessionStorage to persist across page navigation
    sessionStorage.setItem(BLOOM_DISMISSED_KEY, 'true');
    
    // 1. Remove ALL Bloom scripts from the page
    const bloomScripts = document.querySelectorAll('script[src*="bloom"]');
    bloomScripts.forEach(script => script.remove());
    
    // 2. Inject aggressive CSS with !important to hide any remaining elements
    const styleId = 'bloom-widget-hide-style';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        /* Target by source/ID/class */
        iframe[src*="bloom.io"],
        iframe[src*="bloom"],
        [id*="bloom"],
        [id*="Bloom"],
        [class*="bloom"],
        [class*="Bloom"],
        [class*="messenger"],
        [class*="widget"],
        [data-bloom-widget],
        #bloom-widget-container,
        .bloom-messenger-container,
        
        /* Target by position - widgets typically in bottom-right */
        div[style*="position: fixed"][style*="bottom"][style*="right"],
        button[style*="position: fixed"][style*="bottom"],
        iframe[style*="position: fixed"][style*="bottom"],
        
        /* Target by high z-index (chat widgets use very high values) */
        div[style*="z-index: 2147483647"],
        div[style*="z-index: 999999"],
        iframe[style*="z-index"] {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
          width: 0 !important;
          height: 0 !important;
        }
      `;
      document.head.appendChild(style);
    }
    
    // 3. Aggressively remove all matching elements from DOM
    const removeSelectors = [
      'iframe[src*="bloom"]',
      '[data-bloom-widget]',
      '[id*="bloom"]',
      '[id*="Bloom"]',
      '[class*="bloom"]',
      '[class*="Bloom"]',
      '[class*="messenger"]',
    ];
    
    removeSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => element.remove());
    });
    
    // 4. Find and remove fixed-position elements in bottom-right corner
    const allElements = document.querySelectorAll('*');
    allElements.forEach(element => {
      const style = window.getComputedStyle(element);
      if (
        style.position === 'fixed' &&
        (parseInt(style.bottom) >= 0 && parseInt(style.bottom) < 200) &&
        (parseInt(style.right) >= 0 && parseInt(style.right) < 200)
      ) {
        // Check if it's likely the Bloom widget (has high z-index)
        const zIndex = parseInt(style.zIndex);
        if (zIndex > 999999) {
          element.remove();
        }
      }
    });

    // 5. Add aggressive MutationObserver to remove any dynamically added Bloom elements
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            const element = node as HTMLElement;
            // Check if matches Bloom patterns
            if (
              (element.tagName === 'IFRAME' && element.getAttribute('src')?.toLowerCase().includes('bloom')) ||
              element.id?.toLowerCase().includes('bloom') ||
              element.className?.toLowerCase().includes('bloom') ||
              element.className?.toLowerCase().includes('messenger') ||
              element.getAttribute('data-bloom-widget')
            ) {
              element.remove();
            }
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }, []);

  // Render dismiss button only when widget is active
  if (dismissed || !widgetLoaded) return null;

  return (
    <button
      onClick={handleDismiss}
      className="fixed bottom-[76px] right-4 z-[60] bg-background/90 hover:bg-background 
                 rounded-full p-2 shadow-lg border border-border 
                 transition-all hover:scale-110 touch-action-manipulation"
      style={{ minWidth: '44px', minHeight: '44px' }}
      title="Hide chat widget"
      aria-label="Hide chat widget"
    >
      <X size={16} className="text-foreground" />
    </button>
  );
};
